/**
 * Domain types (doc 04 · Data model). These are the wire shapes: every bigint id is a decimal
 * **string** here, because JSON cannot carry a JS `BigInt` and a `number` is not safe for every
 * monday id. The database keeps them as `bigint` (see packages/db).
 */
import { z } from 'zod';
import { accountRoleSchema, boardKindSchema, boardPermissionsSchema, stateSchema, userStatusSchema, workspaceKindSchema, } from './enums.js';
import { assetIdSchema, boardIdSchema, columnIdSchema, folderIdSchema, groupIdSchema, itemIdSchema, teamIdSchema, updateIdSchema, userIdSchema, viewIdSchema, workspaceIdSchema, } from './ids.js';
export const jsonValueSchema = z.unknown();
const timestamp = z.union([z.string(), z.date()]).transform((v) => (v instanceof Date ? v.toISOString() : v));
export const userSchema = z.object({
    id: userIdSchema,
    email: z.string(),
    name: z.string(),
    title: z.string().nullable().default(null),
    phone: z.string().nullable().default(null),
    photoUrl: z.string().nullable().default(null),
    timezone: z.string().nullable().default(null),
    locale: z.string().nullable().default(null),
    role: accountRoleSchema,
    /** monday's `User.status`, lowercase (M0-F3 §4.1). */
    status: userStatusSchema,
    /** monday's `User.kind` string, lowercase and verbatim (`personal_agent_member`, ...). */
    kind: z.string(),
    isDeleted: z.boolean().default(false),
    createdAt: timestamp,
});
export const teamSchema = z.object({
    id: teamIdSchema,
    name: z.string(),
    pictureUrl: z.string().nullable().default(null),
    isGuest: z.boolean().default(false),
    createdAt: timestamp,
});
export const workspaceSchema = z.object({
    id: workspaceIdSchema,
    name: z.string(),
    kind: workspaceKindSchema,
    description: z.string().nullable().default(null),
    settings: z.record(z.string(), jsonValueSchema).default({}),
    state: stateSchema.default('active'),
    createdAt: timestamp,
});
export const folderSchema = z.object({
    id: folderIdSchema,
    workspaceId: workspaceIdSchema,
    parentFolderId: folderIdSchema.nullable().default(null),
    name: z.string(),
    color: z.string().nullable().default(null),
    position: z.string(),
    createdAt: timestamp,
});
export const boardSchema = z.object({
    id: boardIdSchema,
    workspaceId: workspaceIdSchema.nullable().default(null),
    folderId: folderIdSchema.nullable().default(null),
    name: z.string(),
    kind: boardKindSchema,
    /** monday's verbatim `Board.permissions` string; unknown values evaluate as `owners`. */
    permissions: boardPermissionsSchema.default('everyone'),
    state: stateSchema,
    description: z.string().nullable().default(null),
    /** `settings.subitems_board_id` links a board to its subitems board, exactly like monday. */
    settings: z.record(z.string(), jsonValueSchema).default({}),
    itemTerminology: z.string().nullable().default(null),
    position: z.string(),
    createdBy: userIdSchema.nullable().default(null),
    createdAt: timestamp,
    updatedAt: timestamp,
});
export const groupSchema = z.object({
    boardId: boardIdSchema,
    id: groupIdSchema,
    title: z.string(),
    color: z.string().nullable().default(null),
    position: z.string(),
    archived: z.boolean().default(false),
    deleted: z.boolean().default(false),
});
export const columnSchema = z.object({
    boardId: boardIdSchema,
    id: columnIdSchema,
    type: z.string(),
    title: z.string(),
    description: z.string().nullable().default(null),
    settings: z.record(z.string(), jsonValueSchema).default({}),
    position: z.string(),
    width: z.number().int().positive().nullable().default(null),
    archived: z.boolean().default(false),
});
export const itemSchema = z.object({
    id: itemIdSchema,
    boardId: boardIdSchema,
    groupId: groupIdSchema,
    name: z.string(),
    position: z.string(),
    /** Subitems live on the board's linked subitems board and point at their parent. */
    parentItemId: itemIdSchema.nullable().default(null),
    state: stateSchema,
    createdBy: userIdSchema.nullable().default(null),
    createdAt: timestamp,
    updatedAt: timestamp,
});
export const cellValueSchema = z.object({
    itemId: itemIdSchema,
    columnId: columnIdSchema,
    value: jsonValueSchema.nullable(),
    /** Shadow columns written from the column type's `toSortKeys`. */
    textValue: z.string().nullable().default(null),
    numberValue: z.number().nullable().default(null),
    dateValue: timestamp.nullable().default(null),
    updatedAt: timestamp,
    updatedBy: userIdSchema.nullable().default(null),
});
export const viewSchema = z.object({
    id: viewIdSchema,
    boardId: boardIdSchema,
    type: z.string(),
    name: z.string(),
    settings: z.record(z.string(), jsonValueSchema).default({}),
    position: z.string(),
    ownerId: userIdSchema.nullable().default(null),
    isDefault: z.boolean().default(false),
    visibility: z.enum(['shared', 'personal']).default('shared'),
});
export const updateSchema = z.object({
    id: updateIdSchema,
    itemId: itemIdSchema.nullable().default(null),
    parentId: updateIdSchema.nullable().default(null),
    bodyHtml: z.string(),
    bodyText: z.string(),
    createdBy: userIdSchema,
    createdAt: timestamp,
    editedAt: timestamp.nullable().default(null),
});
export const assetSchema = z.object({
    id: assetIdSchema,
    storageKey: z.string(),
    name: z.string(),
    mime: z.string(),
    size: z.number().int().nonnegative(),
    sha256: z.string(),
    uploadedBy: userIdSchema.nullable().default(null),
    createdAt: timestamp,
});
export const boardPermissionSchema = z.object({
    boardId: boardIdSchema,
    /** monday's verbatim `Board.permissions` string (M0-F8 §4.1). */
    permissions: boardPermissionsSchema,
});
/**
 * `activity_log` rows keep monday's vocabulary: `entity` is `board` or `pulse` (item) and
 * `event` is a free-form string. monday publishes **no enum** of event values (docs/09 §A.2),
 * so ours are discovered from the account, never invented.
 */
export const ACTIVITY_ENTITIES = ['board', 'pulse'];
export const activityLogEntrySchema = z.object({
    id: z.string(),
    boardId: boardIdSchema.nullable().default(null),
    itemId: itemIdSchema.nullable().default(null),
    entity: z.enum(ACTIVITY_ENTITIES),
    event: z.string(),
    data: z.record(z.string(), jsonValueSchema).default({}),
    userId: userIdSchema.nullable().default(null),
    createdAt: timestamp,
});
//# sourceMappingURL=domain.js.map